import { type UseFormReturn } from 'react-hook-form'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Trash2, Eye } from 'lucide-react'
import ItemsCompactList from './ItemsCompactList'
import { CampaignFormValues } from '../types'

interface StageFormProps {
	form: UseFormReturn<CampaignFormValues>
	stageIndex: number
	onRemove: () => void
	canRemove: boolean
	stageId?: string
	campaignId?: string
	onPreview?: (stageId: string) => void
	mode?: 'create' | 'edit'
}

export default function StageForm({
	form,
	stageIndex,
	onRemove,
	canRemove,
	stageId,
	campaignId,
	onPreview,
	mode,
}: StageFormProps) {
	return (
		<div className="space-y-4 p-4">
			{/* Stage controls */}
			<div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/50 p-3">
				<div className="flex gap-2">
					{stageId && onPreview && (
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => onPreview(stageId)}
							title="ดูตัวอย่างข้อความ"
						>
							<Eye className="mr-2 h-4 w-4" />
							ดูตัวอย่าง
						</Button>
					)}
				</div>
				{canRemove && (
					<Button
						type="button"
						variant="destructive"
						size="sm"
						onClick={onRemove}
					>
						<Trash2 className="mr-2 h-4 w-4" />
						ลบระยะนี้
					</Button>
				)}
			</div>

			{/* Stage fields */}
			<Card>
				<CardHeader>
					<CardTitle className="text-base">ตั้งค่าระยะการส่ง</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<FormField
						control={form.control}
						name={`stages.${stageIndex}.minutesAfterTrigger`}
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									เวลาส่งข้อความหลังจากไม่มีการฝาก (นาที)
								</FormLabel>
								<FormControl>
									<Input
										type="number"
										placeholder="0"
										min="0"
										{...field}
										onNumberChange={(value) => field.onChange(value)}
									/>
								</FormControl>
								<FormDescription>
									ส่งข้อความหลังจากผู้เล่นมีเงินหมดและยังไม่ฝากเงินเพิ่ม{' '}
									<span className="font-medium text-foreground">
										{field.value || 0} นาที
									</span>
									{field.value === 0 && ' (ส่งทันทีเมื่อเงินหมด)'}
									{field.value > 0 &&
										field.value < 60 &&
										' เพื่อชักชวนให้กลับมาเล่น'}
									{field.value >= 60 &&
										` (${Math.floor(field.value / 60)} ชั่วโมง${field.value % 60 > 0 ? ` ${field.value % 60} นาที` : ''} หลังเงินหมด)`}
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name={`stages.${stageIndex}.message`}
						render={({ field }) => (
							<FormItem>
								<FormLabel required>ข้อความที่จะส่ง</FormLabel>
								<FormControl>
									<Textarea
										placeholder="ตัวอย่าง: เงินหมดแล้วหรอคะ? 😊 มาดูโบนัสที่เรามีให้กันเถอะ!"
										className="min-h-[100px]"
										{...field}
									/>
								</FormControl>
								<FormDescription>
									ข้อความนี้จะแสดงให้ผู้เล่นเห็นพร้อมกับรายการโบนัส
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name={`stages.${stageIndex}.maxItemsToShow`}
						render={({ field }) => (
							<FormItem>
								<FormLabel required>จำนวนรายการโบนัสที่แสดง</FormLabel>
								<FormControl>
									<Input
										type="number"
										placeholder="3"
										min="1"
										max="10"
										{...field}
										onNumberChange={(value) => field.onChange(value)}
									/>
								</FormControl>
								<FormDescription>
									จำนวนรายการโบนัสสูงสุดที่จะแสดงให้ผู้เล่น (แนะนำ 1-3 รายการ)
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
				</CardContent>
			</Card>

			{/* Items */}
			<Card>
				<CardHeader>
					<CardTitle className="text-base">รายการโบนัส/โปรโมชั่น</CardTitle>
					<p className="mt-1 text-sm text-muted-foreground">
						เพิ่มรายการโบนัสที่จะแสดงให้ผู้เล่นเลือก
					</p>
				</CardHeader>
				<CardContent>
					<ItemsCompactList
						form={form}
						stageIndex={stageIndex}
						campaignId={campaignId}
						mode={mode}
					/>
				</CardContent>
			</Card>
		</div>
	)
}
